import { Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'

import { AgamiSpot } from '../types/supabase'
import { removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import { formatDateString } from '../utils/helpers/dates.helpers'
import AgamiMediaTypeChip from './AgamiMediaTypeChip'
import AgamiTypeChip from './AgamiTypeChip'
import SpotPositionBanner from './SpotPositionBanner'

interface AgamiCardDetailsProps {
  spot: AgamiSpot
  nameTestClass: string
}

/**
 * The safe, presentational half of an agami card. Interactive wrappers decide
 * whether the card navigates or exposes owner-only actions; this component only
 * renders metadata already present in the spot payload.
 */
export const AgamiCardDetails: React.FC<AgamiCardDetailsProps> = ({ spot, nameTestClass }) => {
  const { t } = useTranslation()
  const manualCode = spot.experience_content_spots?.[0]?.manual_code
    ? removeManualCodeLibrarySuffix(spot.experience_content_spots[0].manual_code)
    : undefined

  return (
    <div className="flex min-w-0 flex-1 items-center space-x-4">
      {spot.type === 1 && (
        <div className="h-[90px] w-[90px] shrink-0 overflow-hidden rounded-full">
          <SpotPositionBanner
            latitude={typeof spot.latitude === 'number' ? spot.latitude : undefined}
            longitude={typeof spot.longitude === 'number' ? spot.longitude : undefined}
          />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Typography className="break-words font-bold" data-testclass={nameTestClass}>
          {spot.name}
        </Typography>

        <div>
          <Typography variant="small" className="mr-1 inline-block">
            {t('agami.type')}
          </Typography>
          <AgamiTypeChip agamiType={spot.type} />
        </div>

        <div>
          <Typography variant="small" className="mr-1 inline-block">
            {t('agami.media')}
          </Typography>
          <AgamiMediaTypeChip mediaType={spot.content_media_type} />
        </div>

        {spot.type === 2 && manualCode && (
          <Typography variant="small">
            {t('agami.playbackCode')}{' '}
            <strong className="text-black dark:text-base-gray-400">{manualCode}</strong>
          </Typography>
        )}

        <Typography variant="small">
          {t('agami.lastUpdate')}{' '}
          <strong className="text-black dark:text-base-gray-400">
            {spot.updated_at ? formatDateString(spot.updated_at) : ''}
          </strong>
        </Typography>
      </div>
    </div>
  )
}
